import React from "react";

export default function EvolutionFramework(props: any) {
  const stages = [
    {
      title: "Emerging",
      points: [
        "Fragmented, manual operational processes",
        "Limited use of report data",
        "Early technology awareness",
        "Minimal automation, mostly manual tasks",
      ],
    },
    {
      title: "Structured",
      points: [
        "Consistent staff role definition often required",
        "Performance metrics with automated reporting typically necessary",
        "Detailed process definition is a priority",
        "Increase automation when processes are structured and installed",
      ],
    },
    {
      title: "Systemized",
      points: [
        "Integrated and standardized operational processes",
        "Comprehensive data-driven decision making",
        "Advanced technological capabilities",
        "Automated workflows across departments with minimal manual intervention",
      ],
    },
    {
      title: "Optimized",
      points: [
        "Proactive, self-optimizing operational ecosystem",
        "Real-time strategic insights and analytics",
        "Autonomous technological capabilities",
        "Intelligent automation that self-adjusts based on performance data",
      ],
    },
  ];
  const getStageLabel = (total: number) => {
    if (total >= 80) return "Optimized";
    if (total >= 60) return "Systemized";
    if (total >= 40) return "Structured";
    return "Emerging";
  };

  const stageData = [
    {
      name: "Emerging",
      height: "h-24",
      bgClass: "bg-momentum-light-green",
      badgeColor: "bg-momentum-light-green text-white",
    },
    {
      name: "Structured",
      height: "h-36",
      bgClass: "stage-structured",
      badgeColor: "bg-yellow-400 text-gray-800",
    },
    {
      name: "Systemized",
      height: "h-48",
      bgClass: "stage-systemized",
      badgeColor: "bg-purple-500 text-white",
    },
    {
      name: "Optimized",
      height: "h-60",
      bgClass: "stage-optimized",
      badgeColor: "bg-teal-500 text-white",
    },
  ];

  const currentStage = getStageLabel(props.total);

  return (
    <div className="mt-12">
      <h2 className="flex items-center gap-3 text-3xl font-bold text-momentum-dark mb-8">
        <div className="w-1 h-6 bg-momentum-accent rounded-sm"></div>
        Agency Evolution Framework
      </h2>
      <div className="bg-card rounded-xl p-8 border border-momentum-accent/15">
        <div className="mb-12 py-8">
          <div className="flex items-end justify-center h-80 max-w-4xl mx-auto">
            {stageData.map((stage, index) => {
              const isCurrent = stage.name.trim().toLowerCase() === currentStage.trim().toLowerCase();

              return (
                <div key={index} className="flex-1 flex items-end mx-2">
                  <div
                    className={`relative w-full flex flex-col items-center justify-end px-6 py-6 rounded-t-lg shadow-lg transition-all duration-300 ${
                      stage.height
                    } ${stage.bgClass} ${isCurrent ? "border-4 border-yellow-400" : ""}`}
                  >
                    {isCurrent && (
                      <div
                        className={`absolute -top-9 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold whitespace-nowrap tracking-wider ${stage.badgeColor}`}
                      >
                        CURRENT POSITION
                      </div>
                    )}
                    <h3 className="text-white font-bold text-lg text-center text-shadow">{stage.name}</h3>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="text-center mb-8">
          <h4 className="text-xl font-semibold text-momentum-dark mb-2">
            Your Current Position: <span className="text-purple-600 font-bold">{getStageLabel(props.total)} stage</span>
          </h4>

          <p className="text-lg text-muted-foreground">
            Assessment Score: <strong>{props.total} out of 100 points</strong>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stages.map((stage, index) => {
            const isCurrent = stage.title.trim().toLowerCase() === currentStage.trim().toLowerCase();
            return (
              <div
                key={index}
                className={`${
                  isCurrent ? "bg-purple-50 border-purple-400 border-4" : "bg-muted border-momentum-light-green"
                } p-5 rounded-lg border-2`}
              >
                <h5 className={`font-bold mb-3 ${isCurrent ? "text-purple-600" : "text-momentum-accent"}`}>{stage.title}</h5>
                <ul className="space-y-2 text-sm leading-relaxed">
                  {stage.points.map((point, idx) => (
                    <li key={idx} className="pl-5 relative text-muted-foreground">
                      <span className={`absolute left-0 font-bold ${isCurrent ? "text-purple-600" : "text-momentum-accent"}`}>□</span>
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
